import {I18n} from 'aws-amplify/utils';

import {productCopy} from '../../config/productCopy';

import {
    isAwaitingTrustPolicy,
    ProviderConnection,
    ProviderConnectionStatus,
} from './types';

// FNP-1248 — HOW A STATUS IS PRESENTED, in one place.
//
// ⚠️ `I18n.get` MUST RUN AT CALL TIME, never at module scope. A bare object
// literal holding `I18n.get(...)` results is evaluated at import, before the
// language is configured, and captures every string in the default locale — the
// trap documented in the zones module's writeErrorMessages.ts. Every function
// here therefore builds its strings when called.
//
// ⚠️ THE CENTRAL RULE OF THIS FILE: A FIRST-TIME ASSUMED_ROLE CONNECTION THAT
// CAME BACK NOT-ACTIVE IS NOT AN ERROR, AND MUST NOT LOOK LIKE ONE.
//
// Flexi mints the external id server-side during connectProvider, derived from
// the connection id — so it cannot exist before the connection does, and the
// tenant cannot have put it in their trust policy yet. The first result is
// INVALID or UNREACHABLE BY CONSTRUCTION. Colouring that red and calling it a
// failure tells the tenant they did something wrong when they did exactly what
// was asked, and buries the one value they now need.

export type StatusTone = 'success' | 'pending' | 'warning' | 'error';

export type StatusPresentation = {
    // Drives the Badge/Alert colour. `pending` is its own tone precisely so the
    // awaiting-trust-policy case is not painted with the failure colour.
    tone: StatusTone,
    // The short label in the table and next to the heading.
    label: string,
    // One sentence saying what this state MEANS, in the tenant's terms.
    summary: string,
    // Whether this state is the designed intermediate step rather than a fault.
    isExpectedNextStep: boolean,
};

const toneColor: Record<StatusTone, string> = {
    success: 'green',
    pending: 'blue',
    warning: 'yellow',
    error: 'red',
};

export const statusColor = (tone: StatusTone): string => toneColor[tone];

/**
 * How to present a connection's status.
 *
 * ⚠️ TAKES THE WHOLE CONNECTION, NOT JUST THE STATUS ENUM, and that is the
 * point. `INVALID` alone is not enough information to render honestly: the same
 * enum value means "you have not added the external id yet" on a fresh
 * ASSUMED_ROLE connection and "this credential is rejected" on a token one. A
 * `statusLabel(status)` helper taking only the enum could not tell them apart,
 * and every call site would re-derive the distinction independently.
 */
export const presentStatus = (connection: ProviderConnection): StatusPresentation => {

    if (connection.status === 'ACTIVE') {
        return {
            tone: 'success',
            label: I18n.get('Connected'),
            summary: productCopy('{product} can reach this provider with the credential you supplied.'),
            isExpectedNextStep: false,
        };
    }

    // ⚠️ THE AWAITING-TRUST-POLICY CASE, CHECKED BEFORE THE GENERIC FAILURES.
    //
    // Ordering matters: this is a subset of INVALID/UNREACHABLE, so a plain
    // switch on `status` above it would swallow the case entirely and render
    // the designed first step as a red failure.
    if (isAwaitingTrustPolicy(connection)) {
        return {
            tone: 'pending',
            label: I18n.get('Waiting for trust policy'),
            // States the ORDERING as the reason, so the tenant understands this
            // is the sequence working rather than something going wrong.
            summary: productCopy('This is the expected next step. {product} generated an external ID for this connection — it could not exist until the connection did. Add it to your role\'s trust policy, then re-validate below.'),
            isExpectedNextStep: true,
        };
    }

    if (connection.status === 'UNREACHABLE') {
        return {
            tone: 'warning',
            label: I18n.get('Unreachable'),
            // Explicitly NOT blamed on the credential: the schema separates
            // UNREACHABLE from INVALID precisely because the credential may be
            // perfectly good.
            summary: productCopy('{product} could not reach the provider to check this credential. The credential itself may be fine — this is worth retrying.'),
            isExpectedNextStep: false,
        };
    }

    // ⚠️ FNP-1285 — AN ASSUMED_ROLE CONNECTION THE PROVIDER ACTUALLY REFUSED.
    //
    // This now catches the case that used to be swallowed by the awaiting
    // branch above: a connection whose role ARN names something STS will not
    // assume. The old copy told the tenant to add the external ID to their
    // trust policy, which cannot work when there is no role at that ARN.
    //
    // The copy names BOTH possibilities rather than guessing between them,
    // because STS refuses identically whether the role is absent or merely
    // does not trust us. Guessing would be a confident wrong answer, which is
    // the defect this ticket exists to remove — not an improvement on it.
    if (connection.credentialType === 'ASSUMED_ROLE') {
        return {
            tone: 'error',
            label: I18n.get('Rejected'),
            summary: productCopy('The provider refused this role. Either the role ARN does not name a role that exists, or its trust policy does not yet admit {product} with this external ID. Check the ARN below first — AWS answers the same way in both cases, so {product} cannot tell them apart.'),
            isExpectedNextStep: false,
        };
    }

    // INVALID on an API_TOKEN connection: the provider rejected the token
    // itself, and replacing it is the remedy.
    return {
        tone: 'error',
        label: I18n.get('Rejected'),
        summary: I18n.get('The provider rejected this credential. Re-validating without changing anything will not help — update the credential below.'),
        isExpectedNextStep: false,
    };
};

/**
 * Whether the external-id panel is on screen for this connection, and therefore
 * whether it — not the status card — is the thing explaining this state.
 *
 * ⚠️ FNP-1286 — THE DUPLICATION THIS EXISTS TO PREVENT.
 *
 * `ExternalIdPanel` and the detail view's status card each independently decided
 * to explain the same state. Both rendered `presentStatus(...).summary`, and both
 * appended the provider's verbatim `statusDetail`. On the first screen a new
 * tenant ever sees, the explanation appeared twice and the provider's response
 * appeared twice — and evidence repeated reads as two different errors at a
 * glance rather than one state.
 *
 * The split is settled here rather than in either component, because a rule
 * spelled out in two places is a rule that drifts:
 *
 *   The BADGE says what state this is.
 *   The PANEL, when it renders, owns the explanation, the id, the copy control,
 *     the steps, and the provider's own words — everything the tenant reads
 *     while acting, in the one place they act.
 *   The STATUS CARD carries the explanation and the provider's words ONLY when
 *     no panel is there to carry them.
 *
 * ⚠️ THIS MIRRORS `ExternalIdPanel`'s OWN EARLY RETURN, and must keep mirroring
 * it. The panel renders exactly when the connection has an external id — which
 * is NOT the same as `isAwaitingTrustPolicy`: an ACTIVE ASSUMED_ROLE connection
 * keeps its id, so the panel still renders (greyed, as a reference value) and
 * still printed a duplicate `statusDetail` when one was present. Keying this on
 * "awaiting" rather than "has an id" would have left the ACTIVE case doubled.
 */
export const rendersExternalIdPanel = (connection: ProviderConnection): boolean => {
    return Boolean(connection.secretRef?.externalId);
};

/**
 * The wording for the re-validate control.
 *
 * UNREACHABLE is worth retrying AS-IS; INVALID is not — clicking again changes
 * nothing, and the tenant must fix something first. Same button, honest label.
 */
export const revalidateLabel = (status: ProviderConnectionStatus): string => {
    if (status === 'UNREACHABLE') {
        return I18n.get('Retry connection');
    }
    return I18n.get('Re-validate');
};

/**
 * A capability snapshot's headline, gated on whether it is still TRUE.
 *
 * ⚠️ A NON-ACTIVE CONNECTION'S SNAPSHOT IS HISTORY, NOT A CURRENT CLAIM. It
 * records what the credential proved AT CONNECT TIME. Rendering it as present
 * tense next to a broken status would tell a tenant their read-only connection
 * "can create zones" while nothing works at all.
 */
export const capabilitySnapshotCaption = (connection: ProviderConnection): string => {
    if (connection.status === 'ACTIVE') {
        return I18n.get('What this credential can do');
    }
    return I18n.get('What this credential could do when it last worked — not a claim about now');
};
